// ============================================================================
// Spacing Visualization — Token → px Resolver
// ============================================================================

import { BLOCK_PADDING_VALUES } from '../constants/blockLayoutDefaults';
import type { BlockLayoutConfig, DirectionalSpacing, SpacingToken } from '../types';

// ---------------------------------------------------------------------------
// Section padding presets → directional px values
// ---------------------------------------------------------------------------
/** @deprecated Legacy presets — kept only for reference. Use LEGACY_PRESET_TO_TOKEN mapping instead. */
export const SECTION_PADDING_PRESETS: Record<string, { top: number; right: number; bottom: number; left: number }> = {
  compact:  { top: 24, right: 16, bottom: 24, left: 16 },
  standard: { top: 48, right: 24, bottom: 48, left: 24 },
  spacious: { top: 80, right: 32, bottom: 80, left: 32 },
  hero:     { top: 120, right: 48, bottom: 120, left: 48 },
};

/** Maps legacy padding presets → spacing tokens (same mapping as LayoutSettings UI) */
const LEGACY_PRESET_TO_TOKEN: Record<string, SpacingToken> = {
  none: 'none',
  compact: 'sm',
  standard: 'md',
  spacious: 'xl',
  hero: '3xl',
};

// ---------------------------------------------------------------------------
// Resolved spacing type
// ---------------------------------------------------------------------------
export interface ResolvedSpacing {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// ---------------------------------------------------------------------------
// Token names per side — used for tooltip display
// ---------------------------------------------------------------------------
export interface DirectionalTokenNames {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

// ---------------------------------------------------------------------------
// Core resolver: token string → px number
// ---------------------------------------------------------------------------
export function resolveSpacingToPx(token: SpacingToken | undefined): number {
  if (!token || token === 'none') return 0;
  const css = BLOCK_PADDING_VALUES[token];
  if (!css) return 0;
  return parseInt(css, 10) || 0;
}

// ---------------------------------------------------------------------------
// Get effective spacing from a layout config field (uniform or directional)
// ---------------------------------------------------------------------------
export function getEffectiveSpacing(
  value: SpacingToken | DirectionalSpacing | undefined,
  mobileValue: SpacingToken | DirectionalSpacing | undefined,
  isMobile: boolean,
): ResolvedSpacing {
  const raw = isMobile && mobileValue !== undefined ? mobileValue : value;
  if (!raw) return { top: 0, right: 0, bottom: 0, left: 0 };

  if (typeof raw === 'string') {
    const px = resolveSpacingToPx(raw);
    return { top: px, right: px, bottom: px, left: px };
  }

  return {
    top: resolveSpacingToPx(raw.top),
    right: resolveSpacingToPx(raw.right),
    bottom: resolveSpacingToPx(raw.bottom),
    left: resolveSpacingToPx(raw.left),
  };
}

// ---------------------------------------------------------------------------
// Extract token names from a spacing value (uniform or directional).
// Used to thread original token names to SpacingLabel tooltips.
// ---------------------------------------------------------------------------
export function extractTokenNames(
  value: SpacingToken | DirectionalSpacing | undefined,
  mobileValue: SpacingToken | DirectionalSpacing | undefined,
  isMobile: boolean,
): DirectionalTokenNames | undefined {
  const raw = isMobile && mobileValue !== undefined ? mobileValue : value;
  if (!raw) return undefined;

  if (typeof raw === 'string') {
    return { top: raw, right: raw, bottom: raw, left: raw };
  }

  return {
    top: raw.top,
    right: raw.right,
    bottom: raw.bottom,
    left: raw.left,
  };
}

// ---------------------------------------------------------------------------
// Resolve section padding — supports preset strings, tokens, and directional
// ---------------------------------------------------------------------------
export function resolveSectionPadding(
  value: SpacingToken | DirectionalSpacing | string | undefined,
): ResolvedSpacing {
  const zero: ResolvedSpacing = { top: 0, right: 0, bottom: 0, left: 0 };
  if (value === undefined || value === null) return zero;

  // Normalize legacy presets (standard/compact/spacious/hero) → spacing tokens
  // so the overlay matches the editor UI and renderer.
  if (typeof value === 'string') {
    const normalized = (LEGACY_PRESET_TO_TOKEN[value] ?? value) as SpacingToken;
    const px = resolveSpacingToPx(normalized);
    return { top: px, right: px, bottom: px, left: px };
  }

  // DirectionalSpacing object
  return {
    top: resolveSpacingToPx(value.top),
    right: resolveSpacingToPx(value.right),
    bottom: resolveSpacingToPx(value.bottom),
    left: resolveSpacingToPx(value.left),
  };
}

// ---------------------------------------------------------------------------
// Extract token name for section padding (preset name or token)
// ---------------------------------------------------------------------------
export function extractSectionPaddingTokenName(
  value: SpacingToken | DirectionalSpacing | string | undefined,
): DirectionalTokenNames | undefined {
  if (value === undefined || value === null) return undefined;

  if (typeof value === 'string') {
    return { top: value, right: value, bottom: value, left: value };
  }

  return {
    top: value.top,
    right: value.right,
    bottom: value.bottom,
    left: value.left,
  };
}

// ---------------------------------------------------------------------------
// Convenience: resolve both margin + padding from a BlockLayoutConfig
// ---------------------------------------------------------------------------
export function resolveBlockSpacing(
  layout: BlockLayoutConfig | null | undefined,
  isMobile: boolean,
): { margin: ResolvedSpacing; padding: ResolvedSpacing; marginTokens?: DirectionalTokenNames; paddingTokens?: DirectionalTokenNames } {
  if (!layout) {
    return {
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
    };
  }

  return {
    margin: getEffectiveSpacing(layout.margin, layout.mobile_margin, isMobile),
    padding: getEffectiveSpacing(layout.padding, layout.mobile_padding, isMobile),
    marginTokens: extractTokenNames(layout.margin, layout.mobile_margin, isMobile),
    paddingTokens: extractTokenNames(layout.padding, layout.mobile_padding, isMobile),
  };
}
